import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Luggage, Users, Tag as TagIcon, Plane, FileDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

/* Bag Feed On Trip Day — read-only panel that lists every bag record any
   traveller on the trip has linked. Owners see their own + shared ones. */

export default function BagsForTrip({ tripId, tripTitle }) {
  const [state, setState] = useState({ items: [], total_pieces: 0, total_records: 0 });
  const [loading, setLoading] = useState(true);
  const [pdfBusy, setPdfBusy] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/wallet/bags/for-trip/${tripId}`).then((r) => setState(r.data))
      .catch(() => setState({ items: [], total_pieces: 0, total_records: 0 }))
      .finally(() => setLoading(false));
  }, [tripId]);

  const downloadManifest = async () => {
    setPdfBusy(true);
    try {
      const res = await api.get("/wallet/bags/manifest.pdf", { params: { trip_id: tripId }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `baggage-manifest-${(tripTitle || "trip").toLowerCase().replace(/\s/g, "-")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Baggage manifest downloaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not build manifest"); }
    finally { setPdfBusy(false); }
  };

  if (loading) return null;
  const { items, total_pieces, total_records } = state;
  return (
    <section className="border border-indigo-200 bg-white p-4 rounded-2xl" data-testid="trip-bag-feed">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Luggage className="w-5 h-5 text-indigo-600" />
          <h3 className="font-heading font-800 text-lg">Bags on this trip</h3>
          {total_records > 0 && (
            <span className="text-xs font-mono bg-indigo-50 text-indigo-800 border border-indigo-200 px-2 py-0.5 rounded-full">
              {total_records} record{total_records > 1 ? "s" : ""} · {total_pieces} piece{total_pieces > 1 ? "s" : ""}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Link to="/wallet" data-testid="trip-bag-open-wallet"
            className="text-xs font-700 text-indigo-700 hover:underline">Open Wallet →</Link>
          {items.length > 0 && (
            <button onClick={downloadManifest} disabled={pdfBusy} data-testid="trip-bag-manifest"
              className="inline-flex items-center gap-1 text-xs font-700 border border-primary text-primary px-3 py-1.5 rounded-full hover:bg-primary hover:text-white transition-colors disabled:opacity-60">
              {pdfBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />} Manifest PDF
            </button>
          )}
        </div>
      </div>
      {items.length === 0 ? (
        <div className="text-sm text-muted-foreground">
          <p className="mb-2">No bags linked yet. Open <Link to="/wallet" className="font-700 text-indigo-700 hover:underline">My Bags</Link> and set the trip on each record — tags &amp; photos will show up here so you can double-check counts at the belt.</p>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="trip-bag-list">
          {items.map((b) => {
            const tags = (b.tags || []).map((t) => t.number).filter(Boolean);
            const photos = (b.photos || []).filter((p) => p?.key);
            const cover = photos[0];
            return (
              <li key={b.id} className="flex items-start gap-3 border border-zinc-200 bg-zinc-50/60 p-2.5 rounded-xl" data-testid={`trip-bag-${b.id}`}>
                <div className="shrink-0 w-14 h-14 bg-zinc-100 rounded-lg overflow-hidden border border-zinc-200">
                  {cover ? (
                    <img src={`${api.defaults.baseURL}/wallet/bags/${b.id}/photos/${cover.key.split("/").pop()}`}
                      alt={cover.caption || b.owner_name}
                      className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-zinc-400"><Luggage className="w-6 h-6" /></div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-700 text-sm flex items-center gap-1.5 flex-wrap">
                    <Users className="w-3.5 h-3.5 text-primary" />
                    {b.owner_name} · {b.bag_count} bag{b.bag_count > 1 ? "s" : ""}
                    {b._read_only && <span className="text-[10px] text-indigo-700 bg-indigo-50 border border-indigo-200 px-1.5 py-0.5 rounded-full">shared</span>}
                  </div>
                  {(b.airline || b.flight_number) && (
                    <div className="text-xs text-zinc-600 flex items-center gap-1 mt-0.5">
                      <Plane className="w-3 h-3 -rotate-45 text-zinc-500" />
                      {b.airline}{b.flight_number && ` ${b.flight_number}`}
                      {b.from_airport && ` · ${b.from_airport}`}{b.to_airport && ` → ${b.to_airport}`}
                    </div>
                  )}
                  {tags.length > 0 && (
                    <div className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1 flex-wrap">
                      <TagIcon className="w-3 h-3" /> {tags.join(", ")}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
